/**
 * EarningsPage - Appreciation Value & Payout Center
 * 
 * Shows:
 * - Pending balance (on hold)
 * - Eligible balance (ready for payout request)
 * - Payout request form
 * - Payout history
 * 
 * Note: LoveCoins are non-cash and cannot be redeemed for payouts.
 */
import React, { useEffect, useState, useContext, useMemo } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import apiClient from "../api/client";
import { AuthContext } from "../App";
import { 
  Wallet, Clock, CheckCircle, AlertCircle, ArrowLeft, 
  DollarSign, TrendingUp, RefreshCw, ExternalLink
} from "lucide-react";

const API = window.location.origin;

function formatMoney(cents) {
  const dollars = (cents || 0) / 100;
  return dollars.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function formatDate(isoString) {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function StatusBadge({ status }) {
  const styles = {
    submitted: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    in_review: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    approved: "bg-green-500/20 text-green-400 border-green-500/30",
    paid: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    rejected: "bg-red-500/20 text-red-400 border-red-500/30",
    failed: "bg-red-500/20 text-red-400 border-red-500/30",
    canceled: "bg-slate-500/20 text-slate-400 border-slate-500/30",
  };

  const icons = {
    submitted: <Clock className="w-3 h-3" />,
    pending: <Clock className="w-3 h-3" />,
    in_review: <Clock className="w-3 h-3" />,
    approved: <CheckCircle className="w-3 h-3" />,
    paid: <CheckCircle className="w-3 h-3" />,
    rejected: <AlertCircle className="w-3 h-3" />,
    failed: <AlertCircle className="w-3 h-3" />,
    canceled: <AlertCircle className="w-3 h-3" />,
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.pending}`}>
      {icons[status]}
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
}

export default function EarningsPage() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [balances, setBalances] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("STRIPE_CONNECT");
  const [handle, setHandle] = useState("");

  const amountCents = useMemo(() => {
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.round(n * 100);
  }, [amount]);

  const headers = { Authorization: `Bearer ${token}` };

  async function fetchData() {
    try {
      setLoading(true);
      
      const [balRes, histRes] = await Promise.all([
        apiClient.get(`/payouts/balances`, { headers }),
        apiClient.get(`/payouts/history`, { headers }),
      ]);
      
      setBalances(balRes.data);
      setRequests(histRes.data.items || []);
    } catch (err) {
      console.error("[EarningsPage] Failed to load data:", err);
      toast.error("Failed to load appreciation data");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, [token]);

  async function submitRequest() {
    if (amountCents < 2000) {
      toast.error("Minimum payout request is $20");
      return;
    }

    if (method !== "STRIPE_CONNECT" && !handle.trim()) {
      toast.error("Please enter your payout handle");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        amount_cents: amountCents,
        method,
        handle: method === "STRIPE_CONNECT" ? null : handle.trim(),
      };

      await apiClient.post(`/payouts/unified-request`, payload, { headers });
      
      toast.success("Payout request submitted!");
      setAmount("");
      setHandle("");
      await fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === "object" ? detail.message : detail || "Request failed";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
      </div>
    );
  }

  const canRequest = balances?.can_request_payout && amountCents > 0 && amountCents <= (balances?.available_cents || 0);
  const hasPendingRequest = requests.some(r => ["submitted", "pending", "in_review"].includes(r.status));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-slate-100">
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <header className="border-b border-slate-800 pb-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 transition mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-emerald-400 to-teal-500 bg-clip-text text-transparent">
                Appreciation Value
              </h1>
              <p className="text-sm text-slate-400 mt-1">
                See appreciation you've received and manage payout requests
              </p>
            </div>
            <button
              onClick={fetchData}
              className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </header>

        {/* Balance Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5">
            <div className="flex items-center gap-2 text-sm text-slate-400 mb-2">
              <Clock className="w-4 h-4" />
              Pending (on hold)
            </div>
            <div className="text-3xl font-bold text-amber-400">
              {formatMoney(balances?.pending_cents)}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Available after 30-day hold period
            </p>
          </div>

          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5">
            <div className="flex items-center gap-2 text-sm text-slate-400 mb-2">
              <Wallet className="w-4 h-4" />
              Eligible for payout
            </div>
            <div className="text-3xl font-bold text-emerald-400">
              {formatMoney(balances?.available_cents)}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Ready for payout request
            </p>
          </div>
        </div>

        {/* Lifetime Stats */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">Lifetime Appreciation</div>
              <div className="text-xl font-semibold text-slate-200">
                {formatMoney(balances?.lifetime_earned_cents)}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">Total Paid Out</div>
              <div className="text-xl font-semibold text-slate-200">
                {formatMoney(balances?.lifetime_withdrawn_cents)}
              </div>
            </div>
          </div>
        </div>

        {/* Eligibility Status */}
        {!balances?.can_request_payout && balances?.eligibility_blocking_reasons?.length > 0 && (
          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-amber-200">Unable to request payout</div>
                <ul className="mt-1 text-sm text-amber-200/80 list-disc list-inside">
                  {balances.eligibility_blocking_reasons.map((reason, i) => (
                    <li key={i}>{reason.replace(/_/g, " ").toLowerCase()}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Request Payout Form */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-400" />
            Request a Payout
          </h2>

          {hasPendingRequest ? (
            <div className="text-sm text-amber-400 bg-amber-500/10 rounded-xl p-3 border border-amber-500/20">
              You have a pending payout request. Please wait for it to be processed before submitting another.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Amount (USD)</label>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="25.00"
                    min="20"
                    step="0.01"
                    className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-2.5 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                    data-testid="payout-amount-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Payout Method</label>
                  <select
                    value={method}
                    onChange={(e) => setMethod(e.target.value)}
                    className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-2.5 text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                    data-testid="payout-method-select"
                  >
                    <option value="STRIPE_CONNECT">Stripe Connect (Recommended)</option>
                    <option value="CASHAPP">Cash App</option>
                    <option value="PAYPAL">PayPal</option>
                  </select>
                </div>
              </div>

              {method !== "STRIPE_CONNECT" && (
                <div>
                  <label className="block text-sm text-slate-400 mb-1">
                    {method === "CASHAPP" ? "Cash App $cashtag" : "PayPal Email"}
                  </label>
                  <input
                    type="text"
                    value={handle}
                    onChange={(e) => setHandle(e.target.value)}
                    placeholder={method === "CASHAPP" ? "$yourname" : "you@email.com"}
                    className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-2.5 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                    data-testid="payout-handle-input"
                  />
                </div>
              )}

              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-slate-500">
                  Minimum payout: $20 · Reviewed within 24–48 hours
                </p>
                <button
                  onClick={submitRequest}
                  disabled={submitting || !canRequest}
                  className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium transition"
                  data-testid="submit-payout-btn"
                >
                  {submitting ? "Submitting..." : "Submit Request"}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Payout History */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-400" />
            Payout History
          </h2>

          {requests.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No payout requests yet
            </div>
          ) : (
            <div className="space-y-3">
              {requests.map((req) => (
                <div
                  key={req.id}
                  className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-950/50 p-4"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-200">
                        {formatMoney(req.amount_cents)}
                      </span>
                      <StatusBadge status={req.status} />
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {formatDate(req.created_at)} · {req.method}
                    </div>
                    {req.status === "rejected" && req.admin_note && (
                      <div className="text-xs text-red-400 mt-1">
                        Reason: {req.admin_note}
                      </div>
                    )}
                  </div>
                  {req.stripe_transfer_id && (
                    <a
                      href={`https://dashboard.stripe.com/connect/transfers/${req.stripe_transfer_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-slate-500 hover:text-slate-300"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Footer */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4 text-center text-sm text-slate-500">
          <p>
            Payouts are reviewed by our team before processing.
            <br />
            LoveCoins are non-cash and cannot be redeemed for payouts.
          </p>
        </div>
      </div>
    </div>
  );
}
